/**
 * Automations UI client interface. Fixture-backed for the UI track; the live implementation
 * (lib/api/endpoints/*) drops in behind the SAME shape, so components never change when
 * the backend lands. No raw fetch, no localStorage — state lives in TanStack Query.
 */
import type { AutomationPreferences } from "@xavior/schema";
import {
  FIXTURE_AUTOMATIONS,
  FIXTURE_TEMPLATES,
  runningNow,
  type Automation,
  type Autonomy,
  type RunningRun,
} from "./fixtures";
import {
  FIXTURE_RUNS,
  simulateAutomation,
  FIXTURE_APPROVALS,
  buildActivity,
  buildNotifications,
  type AutomationRunDetail,
  type SimulateResult,
  type SimWindow,
  type ApprovalItem,
  type ActivityItem,
  type AutomationNotification,
} from "./fixtures-deep";

export interface AutomationsClient {
  listAutomations(): Promise<Automation[]>;
  getAutomation(id: string): Promise<Automation | null>;
  setEnabled(id: string, enabled: boolean): Promise<Automation | null>;
  setAutonomy(id: string, autonomy: Autonomy): Promise<Automation | null>;
  setColor(id: string, color: string): Promise<Automation | null>;
  /** Replace one automation's per-kind preferences (Phase 2). */
  setPreferences(
    id: string,
    preferences: AutomationPreferences,
  ): Promise<void>;
  /** Replace one automation's `actionAllowlist` — the capability opt-in (default off). The server
   *  validates the list against the known ACTION_VOCABULARY (400 on unknown). */
  setActionAllowlist(
    id: string,
    actionAllowlist: string[],
  ): Promise<Automation | null>;
  /** Scaffold a new, fully-editable automation from a plain-language purpose. */
  createAutomation(purpose: string): Promise<Automation>;
  deleteAutomation(id: string): Promise<void>;
  listTemplates(): Promise<typeof FIXTURE_TEMPLATES>;
  listRuns(agentId: string): Promise<AutomationRunDetail[]>;
  simulate(agentId: string, window: SimWindow): Promise<SimulateResult>;
  listApprovals(): Promise<ApprovalItem[]>;
  /** `preparedPayload` carries the user's edits when the decision is "edited" (else omitted). */
  decideApproval(
    id: string,
    decision: "approved" | "edited" | "rejected",
    preparedPayload?: Record<string, unknown>,
  ): Promise<void>;
  listActivity(): Promise<ActivityItem[]>;
  /** In-flight runs across all automations — the "Running now" strip (live run-status feed). */
  listRunningNow(): Promise<RunningRun[]>;
  /**
   * The three typed notification signals (design §4), priority order resolved in the
   * feed. A `sign_off` notification corresponds to the run holding that approval, so
   * resolving it (decide path) drops it from the unresolved set on the next read.
   */
  listNotifications(): Promise<AutomationNotification[]>;
  /** A chat refinement writes back to Build: updates the condition + marks it just-updated. */
  applyChatRefinement(
    agentId: string,
    condition: string,
  ): Promise<Automation | null>;
  undoRefinement(agentId: string): Promise<Automation | null>;
}

// Session-local mutable copy so toggles/edits persist while you click around (fixtures only).
let automations: Automation[] = structuredClone(FIXTURE_AUTOMATIONS);
let approvals: ApprovalItem[] = structuredClone(FIXTURE_APPROVALS);
// Approval ids the user has decided this session, so a run's inline sign-off clears
// from its timeline once decided (mirrors the engine resuming the suspended run).
const decidedApprovals = new Set<string>();
const delay = (ms = 110) => new Promise<void>((r) => setTimeout(r, ms));
const find = (id: string) =>
  structuredClone(automations.find((a) => a.id === id) ?? null);

export const fixtureAutomationsClient: AutomationsClient = {
  async listAutomations() {
    await delay();
    return structuredClone(automations);
  },
  async getAutomation(id) {
    await delay();
    return find(id);
  },
  async setEnabled(id, enabled) {
    await delay();
    automations = automations.map((a) =>
      a.id === id ? { ...a, status: enabled ? "active" : "paused" } : a,
    );
    return find(id);
  },
  async setAutonomy(id, autonomy) {
    await delay();
    automations = automations.map((a) => (a.id === id ? { ...a, autonomy } : a));
    return find(id);
  },
  async setColor(id, color) {
    await delay();
    automations = automations.map((a) => (a.id === id ? { ...a, color } : a));
    return find(id);
  },
  async setPreferences(id, preferences) {
    await delay();
    // Echo into the session-local copy so the Customize panel reflects the save.
    automations = automations.map((a) =>
      a.id === id ? { ...a, preferences } : a,
    );
  },
  async setActionAllowlist(id, actionAllowlist) {
    await delay();
    // Echo into the session-local copy so the Customize panel's toggles reflect the save.
    automations = automations.map((a) =>
      a.id === id ? { ...a, actionAllowlist } : a,
    );
    return find(id);
  },
  async createAutomation(purpose) {
    await delay();
    const automation = scaffoldAutomation(purpose, automations.length);
    automations = [automation, ...automations];
    return structuredClone(automation);
  },
  async deleteAutomation(id) {
    await delay();
    automations = automations.filter((a) => a.id !== id);
    // Drop any pending approvals belonging to the removed automation so the
    // Approvals view + pending counts stay consistent.
    approvals = approvals.filter((a) => a.agentId !== id);
    delete refineUndo[id];
  },
  async listTemplates() {
    await delay();
    return structuredClone(FIXTURE_TEMPLATES);
  },
  async listRuns(agentId) {
    await delay();
    const runs = structuredClone(FIXTURE_RUNS[agentId] ?? []);
    // Reflect any decided sign-offs: the run leaves "awaiting" and its waiting
    // milestone collapses to a resolved beat (the engine resuming the run).
    return runs.map((r) => {
      if (!r.awaitingSignOff || !decidedApprovals.has(r.awaitingSignOff.approvalId)) {
        return r;
      }
      return {
        ...r,
        status: "approved" as const,
        awaitingSignOff: undefined,
        milestones: r.milestones?.map((m) =>
          m.kind === "waiting"
            ? {
                ...m,
                kind: "did" as const,
                label: "Decision recorded — handed back to the run",
                detail:
                  "Your decision was recorded. In the live engine the suspended run resumes from here.",
              }
            : m,
        ),
      };
    });
  },
  async simulate(agentId, window) {
    await delay(320);
    return simulateAutomation(agentId, window);
  },
  async listApprovals() {
    await delay();
    return structuredClone(approvals);
  },
  async decideApproval(id) {
    await delay();
    // Record only — never sends/executes (design §5.3, module rule). Drop it from
    // the cross-automation queue and mark it decided so the owning run's inline card clears.
    approvals = approvals.filter((a) => a.id !== id);
    decidedApprovals.add(id);
  },
  async listActivity() {
    await delay();
    return buildActivity();
  },
  async listRunningNow() {
    await delay();
    return structuredClone(runningNow);
  },
  async listNotifications() {
    await delay();
    // A sign_off notification mirrors a suspended run's approval. Once that approval
    // is decided (inline card / queue), the run resumes — so drop the notification,
    // matching how listRuns clears the run's `awaitingSignOff`.
    return buildNotifications().filter(
      (n) => !(n.approvalId && decidedApprovals.has(n.approvalId)),
    );
  },
  async applyChatRefinement(agentId, condition) {
    await delay();
    automations = automations.map((a) => {
      if (a.id !== agentId) return a;
      refineUndo[agentId] = {
        condition: a.build.condition,
        justUpdated: a.build.conditionJustUpdated,
      };
      return {
        ...a,
        build: { ...a.build, condition, conditionJustUpdated: true },
      };
    });
    return find(agentId);
  },
  async undoRefinement(agentId) {
    await delay();
    const prev = refineUndo[agentId];
    if (prev) {
      automations = automations.map((a) =>
        a.id === agentId
          ? {
              ...a,
              build: {
                ...a.build,
                condition: prev.condition,
                conditionJustUpdated: prev.justUpdated ?? false,
              },
            }
          : a,
      );
      delete refineUndo[agentId];
    }
    return find(agentId);
  },
};

// Previous Build state per automation, so a chat refinement can be undone (fixtures only).
const refineUndo: Record<
  string,
  { condition?: string; justUpdated?: boolean }
> = {};

// Identity colors handed to freshly-created automations, rotated by current count.
const NEW_AUTOMATION_COLORS = [
  "#3a6ea5",
  "#b4763f",
  "#4f8a6b",
  "#7a5ea8",
  "#a8546a",
  "#2f8f8a",
];

/**
 * Build a complete, immediately-editable automation from a plain-language purpose.
 * The purpose becomes the automation's role; a short name is derived from it. Defaults
 * are deliberately safe (paused, suggest-only, guardrails on, no trigger yet) so
 * the automation does nothing until the user finishes it in the Build lens.
 */
function scaffoldAutomation(purpose: string, count: number): Automation {
  const role = purpose.replace(/\s+/g, " ").trim();
  const id = `a-new-${Date.now()}`;
  return {
    id,
    name: deriveAutomationName(role),
    role: role || "New automation",
    color: NEW_AUTOMATION_COLORS[count % NEW_AUTOMATION_COLORS.length],
    status: "paused",
    autonomy: "suggest",
    triggerMode: "scheduled",
    pendingCount: 0,
    recentActivity: "Not run yet — finish setting it up in Build.",
    build: {
      trigger: "",
      condition: undefined,
      actions: [
        {
          id: `${id}-act-1`,
          label: "Describe the first step…",
          reversibility: "free",
        },
      ],
      guardrails: { neverNewDomains: true, flagUntrusted: true },
    },
    runs: [],
  };
}

// ── Automation naming ────────────────────────────────────────────────────────────
// Name the automation for what it will *do*, weaving in the specific topic from the
// user's message: a domain qualifier + the object it acts on + the action's
// role — e.g. "Investor reply drafter", "Competitor pricing tracker", "Fintech
// lead researcher", "Client onboarding manager" — not a generic "Email bot".

// Acronyms kept upper-cased in the name.
const NAME_ACRONYMS = new Set([
  "ai", "ml", "crm", "hr", "kpi", "saas", "api", "seo", "pr", "qa",
]);

// "Domain" words that read as the automation's subject area and sit at the FRONT of
// the composed name (e.g. "investor" in "Investor reply drafter").
const DOMAIN_QUALIFIERS = new Set([
  "investor", "client", "customer", "recruiter", "candidate", "vendor",
  "supplier", "partner", "team", "teammate", "employee", "user", "subscriber",
  "competitor", "fintech", "crypto", "saas", "ecommerce", "healthcare",
  "startup", "enterprise", "linkedin", "twitter", "instagram", "facebook",
  "tiktok", "youtube", "slack", "github", "gmail", "sales", "marketing",
  "support", "finance", "legal", "product", "engineering", "design", "hr",
  "exec", "executive", "board", "press", "media", "ai", "ml", "crm",
]);

// Connectives / fillers / generic verbs that must never become part of the name
// (the action verbs are captured separately as the role).
const NAME_STOPWORDS = new Set([
  "a", "an", "the", "to", "for", "of", "on", "in", "with", "and", "or", "my",
  "our", "your", "me", "us", "we", "it", "its", "that", "this", "these", "those",
  "who", "whom", "which", "when", "then", "at", "by", "from", "into", "about",
  "as", "is", "are", "be", "so", "if", "up", "out", "over", "per", "via",
  "new", "newly", "upcoming", "potential", "important", "various", "several",
  "some", "few", "any", "all", "each", "every", "more", "most", "key", "main",
  "relevant", "quick", "simple", "just", "please", "help", "helps", "want",
  "wants", "need", "needs", "would", "like", "make", "get", "keep", "stay",
  "something", "stuff", "thing", "things", "good", "best", "better", "top",
  "eye", "performance", "daily", "weekly", "monthly", "morning", "day", "days",
  "week", "weeks", "month", "friday", "am", "twice", "went", "quiet",
  // action verbs (the role is derived from these separately)
  "draft", "write", "writing", "compose", "respond", "answer", "send",
  "sending", "schedule", "scheduling", "book", "remind", "reminding", "track",
  "tracking", "monitor", "monitoring", "watch", "watching", "alert", "sort",
  "sorting", "label", "labelling", "labeling", "triage", "organize", "organise",
  "file", "research", "gather", "compile", "investigate", "look", "find",
  "finding", "audit", "proofread", "check", "analyze", "analyse", "clean",
  "publish", "notify", "flag", "manage", "managing", "handle", "coordinate",
  "summarize", "summarise", "digest", "recap", "prepare", "prep", "ask", "asks",
  "meet", "propose", "read", "create", "arrive", "arrives",
]);

// Words left un-singularized (the trailing "s" is part of the word).
const NAME_NO_SINGULAR = new Set([
  "news", "analytics", "sales", "series", "status", "media", "data",
]);

// Recognizable object nouns — used to reject gibberish when there's no role
// (and so a role-less domain prompt still yields a named automation, not "Custom automation").
const KNOWN_OBJECTS = new Set([
  "invoice", "expense", "receipt", "payment", "budget", "billing", "code",
  "commit", "repo", "request", "pricing", "lead", "prospect", "deadline",
  "task", "todo", "meeting", "call", "contact", "network", "onboarding",
  "analytics", "metric", "kpi", "dashboard", "report", "content", "post",
  "update", "blog", "tweet", "social", "newsletter", "email", "inbox", "mail",
  "message", "calendar", "reply", "review", "news", "regulation", "article",
  "deal", "account",
]);

// Capitalized-but-not-a-name words to ignore when scanning for a proper noun.
const NAME_PROPER_SKIP = new Set([
  "monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday",
  "january", "february", "march", "april", "may", "june", "july", "august",
  "september", "october", "november", "december", "today", "tomorrow",
  "tonight", "yesterday", "mon", "tue", "wed", "thu", "fri", "sat", "sun",
]);

// Well-known automation jobs that read better with their established idiom; a domain
// qualifier from the message is prepended for specificity when present.
const NAME_RECIPES: Array<[RegExp, string]> = [
  [/\b(summari[sz]e?|digest|recap|round[\s-]?up)\b.*\b(week|weekly|friday)\b|\b(week|weekly|friday)\b.*\b(summari[sz]e?|digest|recap|report|round[\s-]?up)/, "Weekly digest"],
  [/\b(summari[sz]e?|digest|recap|brief|rundown)\b.*\b(daily|morning|every day|each day)|\b(daily|morning|every day|each day|7\s?am)\b.*\b(brief|digest|summary|rundown|glance)/, "Morning brief"],
  [/\b(triage|sort|label|categori|organi[sz]e|clean up|tidy)\b.*\b(inbox|email|mail)|\binbox\b.*\b(triage|sort|label|clean)/, "Inbox triager"],
  [/\b(prep|prepare|brief)\b.*\b(meeting|call|1:1|interview)|\bmeeting\b.*\bprep/, "Meeting prep"],
  [/\bfollow[\s-]?up\b|\bchase|\bnudge\b|\bcircle back\b|\bcheck(ing)? in\b/, "Follow-up chaser"],
  [/\b(draft|write|compose|reply|respond)\b.*\b(repl|email|message|response)/, "Reply drafter"],
  [/\bnews\b|\bheadline|\bpress\b/, "News watcher"],
];

// Action → the role noun an automation doing that action would carry. The first
// matching word is also stripped from the topic so it isn't repeated.
const ROLE_VERBS: Array<[RegExp, string]> = [
  [/\bsummari[sz]|\bdigest\b|\brecap\b/, "digest"],
  [/\bdraft|\bwrite\b|\bcompose/, "drafter"],
  [/\breply|\brespond/, "responder"],
  [/\bschedul|\bbook\b/, "scheduler"],
  [/\bremind/, "reminder"],
  [/\btrack|\bmonitor|\bwatch|\bkeep an eye|\balert/, "tracker"],
  [/\bsort|\blabel|\btriage|\borgani[sz]|\bfile\b/, "organizer"],
  [/\bresearch|\bgather|\bcompile|\binvestigate|\blook up|\bfind out/, "researcher"],
  [/\breview|\baudit|\bproofread/, "reviewer"],
  [/\banaly[sz]|\breport/, "analyst"],
  [/\bpost\b|\bpublish|\bshare\b/, "publisher"],
  [/\bnotify|\bflag\b/, "notifier"],
  [/\bmanage|\bhandle|\bcoordinate/, "manager"],
];

const titleCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const displayWord = (w: string) => (NAME_ACRONYMS.has(w) ? w.toUpperCase() : w);

function singularizeWord(w: string): string {
  if (NAME_NO_SINGULAR.has(w)) return w;
  if (w.endsWith("ies")) return `${w.slice(0, -3)}y`;
  if (w.endsWith("s") && !w.endsWith("ss")) return w.slice(0, -1);
  return w;
}

/** Salient content words from the message, in order, cleaned + singularized. */
function nameTokens(text: string): string[] {
  return text
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter(
      (w) =>
        (w.length > 2 || DOMAIN_QUALIFIERS.has(w) || NAME_ACRONYMS.has(w)) &&
        !NAME_STOPWORDS.has(w),
    )
    .map(singularizeWord)
    .filter((w, i, a) => a.indexOf(w) === i);
}

/**
 * The first named person / company / group in the message — a mid-sentence
 * capitalized word or an acronym (e.g. "John", "Acme", "IBM"). This is the most
 * specific qualifier we can put in the name ("John reply drafter", "Acme weekly
 * digest"), so it wins over the generic domain words. Returns the word with its
 * original casing, or null. Sentence-initial capitals (grammatical, not names)
 * and common/day/month words are ignored.
 */
function detectProperNoun(original: string): string | null {
  const words = original.split(/\s+/);
  for (let i = 0; i < words.length; i++) {
    const raw = words[i].replace(/^[^A-Za-z0-9]+|[^A-Za-z0-9'&-]+$/g, "");
    if (!raw) continue;
    // Skip the first word of a *non-initial* sentence (its capital is grammar).
    // The leading word IS considered so a real name can lead ("Acme deal
    // tracker"); common leading words are rejected just below.
    if (i > 0 && /[.!?]$/.test(words[i - 1])) continue;
    const isAcronym = /^[A-Z]{2,5}$/.test(raw);
    const isCapitalized = /^[A-Z][a-z]+$/.test(raw);
    if (!isAcronym && !isCapitalized) continue;
    const lower = raw.toLowerCase();
    const singular = singularizeWord(lower);
    // Only treat genuine names as proper nouns. Reject capitalized common words
    // (verbs/fillers, domain words, object nouns) so they flow through the normal
    // topic logic instead of being prepended verbatim — otherwise "Triage my
    // Inbox" became "Inbox inbox triager" and "review the Reports" duplicated.
    if (
      NAME_STOPWORDS.has(lower) ||
      NAME_PROPER_SKIP.has(lower) ||
      DOMAIN_QUALIFIERS.has(singular) ||
      KNOWN_OBJECTS.has(singular)
    ) {
      continue;
    }
    return raw;
  }
  return null;
}

/**
 * Name the automation for what it will *do*, integrating the specific topic the user
 * described. Canonical idioms (Weekly digest, Inbox triager, …) win first and
 * gain a domain qualifier for specificity; otherwise we compose "<qualifier>
 * <object> <role>" from the message — e.g. "track competitor pricing" →
 * "Competitor pricing tracker", "research leads in fintech" → "Fintech lead
 * researcher". Filler/adjectives are dropped so only meaningful words land.
 */
export function deriveAutomationName(purpose: string): string {
  const text = purpose.toLowerCase();
  if (!text.trim()) return "New automation";

  // The action verb → role noun, plus the exact word that triggered it (so the
  // verb isn't repeated as a topic word, e.g. "review" in "review pull requests").
  let role: string | undefined;
  let trigger: string | undefined;
  for (const [pattern, roleName] of ROLE_VERBS) {
    const match = text.match(pattern);
    if (match) {
      role = roleName;
      trigger = singularizeWord(match[0].trim().split(/\s+/)[0]);
      break;
    }
  }

  // A named person/company/group is the most specific qualifier — keep its
  // casing and drop it from the topic tokens so it isn't duplicated. Compare on
  // the singularized form (tokens are singularized) so "Investors" matches the
  // "investor" token.
  const properNoun = detectProperNoun(purpose);
  const properLower = properNoun
    ? singularizeWord(properNoun.toLowerCase())
    : undefined;

  const content = nameTokens(text).filter(
    (t) => t !== trigger && t !== properLower,
  );
  const domainQualifier = content.find((t) => DOMAIN_QUALIFIERS.has(t));
  const objects = content.filter((t) => !DOMAIN_QUALIFIERS.has(t));
  // Proper noun wins (a specific person/group), else a known domain word.
  const qualifier = properNoun ?? domainQualifier;

  // Canonical idioms — prepend the qualifier for specificity.
  for (const [pattern, name] of NAME_RECIPES) {
    if (pattern.test(text)) {
      return qualifier
        ? titleCase(`${displayWord(qualifier)} ${name.toLowerCase()}`)
        : name;
    }
  }

  // Compose "<qualifier> <object> <role>" from the message.
  let topic: string[] = qualifier
    ? [qualifier, objects[0]].filter((w): w is string => Boolean(w))
    : objects.slice(0, 2);
  if (topic.length === 0) topic = content.slice(0, 2);

  // No clear role and nothing recognizable → don't invent a name from noise.
  if (!role && !qualifier && !topic.some((t) => KNOWN_OBJECTS.has(t))) {
    return "Custom automation";
  }

  const parts = [...topic.map(displayWord), role ?? "assistant"].filter(
    (w, i, a) => a.indexOf(w) === i,
  );
  let name = parts.join(" ");
  name = name.charAt(0).toUpperCase() + name.slice(1);
  if (name.length > 36) name = name.slice(0, 36).trimEnd();
  return name;
}

/** The single binding components import. Swap to the live client here later. */
export const automationsClient: AutomationsClient = fixtureAutomationsClient;
